import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild, inject, AfterViewInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as d3 from 'd3';
import { ThemeService } from '../../../../core/services/theme.service';
import { Subscription } from 'rxjs';

export interface RadialPieData {
    label: string;
    value: number;
    color?: string;
    percentage?: number;
}

@Component({
    selector: 'app-radial-pie-chart',
    standalone: true,
    imports: [CommonModule],
    template: `
    <div class="radial-chart-container w-full h-full relative" #chartContainer>
      <svg #svgElement class="w-full h-full"></svg>
      <div #tooltip class="chart-tooltip"></div>
    </div>
  `,
    changeDetection: ChangeDetectionStrategy.Eager,
    styles: [`
    :host {
      display: block;
      width: 100%;
      height: 100%;
    }
    .radial-chart-container {
      min-height: 250px;
    }
    .chart-tooltip {
      position: absolute;
      padding: 8px 12px;
      background: white;
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      font-size: 12px;
      pointer-events: none;
      display: none;
      z-index: 10;
      box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
      color: #1e293b;
    }
  `]
})
export class RadialPieChartComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
    @ViewChild('chartContainer') chartContainer!: ElementRef<HTMLDivElement>;
    @ViewChild('svgElement') svgElement!: ElementRef<SVGSVGElement>;
    @ViewChild('tooltip') tooltipElement!: ElementRef<HTMLDivElement>;

    @Input() data: RadialPieData[] = [];
    @Input() levels: number = 4;
    @Input() maxValue?: number;
    @Input() colors: string[] = ['#3B82F6', '#60A5FA', '#93C5FD', '#BFDBFE', '#DBEAFE'];

    private themeService = inject(ThemeService);
    private resizeObserver!: ResizeObserver;
    private themeSubscription!: Subscription;
    private currentTheme: string = 'light';

    ngOnInit() {
        this.themeSubscription = this.themeService.theme$.subscribe(theme => {
            this.currentTheme = theme;
            this.drawChart();
        });
    }

    ngAfterViewInit() {
        this.resizeObserver = new ResizeObserver(() => this.drawChart());
        this.resizeObserver.observe(this.chartContainer.nativeElement);
        this.drawChart();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['data'] || changes['maxValue'] || changes['levels']) {
            this.drawChart();
        }
    }

    ngOnDestroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
        if (this.themeSubscription) {
            this.themeSubscription.unsubscribe();
        }
    }

    private drawChart() {
        if (!this.svgElement || !this.data || this.data.length === 0) return;

        const container = this.chartContainer.nativeElement;
        const width = container.clientWidth;
        const height = container.clientHeight;
        const radius = Math.min(width, height) / 2 - 40;

        const svg = d3.select(this.svgElement.nativeElement);
        svg.selectAll('*').remove();

        svg.attr('viewBox', `0 0 ${width} ${height}`)
            .attr('width', width)
            .attr('height', height);

        const g = svg.append('g')
            .attr('transform', `translate(${width / 2},${height / 2})`);

        const totalValue = d3.max(this.data, d => d.value) || 100;
        const effectiveMax = this.maxValue || totalValue;

        const rScale = d3.scaleLinear()
            .range([0, radius])
            .domain([0, effectiveMax]);

        const angleStep = (Math.PI * 2) / this.data.length;
        const themeColors = this.getThemeColors();

        // 1. Draw Background Grid (Circles)
        const gridLines = g.append('g').attr('class', 'grid-lines');
        for (let i = 1; i <= this.levels; i++) {
            const r = (radius / this.levels) * i;
            gridLines.append('circle')
                .attr('cx', 0)
                .attr('cy', 0)
                .attr('r', r)
                .attr('fill', 'none')
                .attr('stroke', themeColors.grid)
                .attr('stroke-width', 1);
        }

        // 2. Draw Spokes
        const spokes = g.append('g').attr('class', 'spokes');
        this.data.forEach((_, i) => {
            const angle = i * angleStep - Math.PI / 2;
            spokes.append('line')
                .attr('x1', 0)
                .attr('y1', 0)
                .attr('x2', radius * Math.cos(angle))
                .attr('y2', radius * Math.sin(angle))
                .attr('stroke', themeColors.grid)
                .attr('stroke-width', 1);
        });

        // 3. Draw Axis Labels
        const labels = g.append('g').attr('class', 'labels');
        this.data.forEach((d, i) => {
            const angle = i * angleStep + angleStep / 2 - Math.PI / 2;
            const labelRadius = radius + 20;
            labels.append('text')
                .attr('x', labelRadius * Math.cos(angle))
                .attr('y', labelRadius * Math.sin(angle))
                .attr('fill', themeColors.text)
                .attr('text-anchor', 'middle')
                .attr('alignment-baseline', 'middle')
                .style('font-size', '10px')
                .style('font-weight', '500')
                .text(d.label);
        });

        // 4. Draw Wedges
        const wedges = g.append('g').attr('class', 'wedges');

        this.data.forEach((d, i) => {
            const color = d.color || this.colors[i % this.colors.length];

            const arc = d3.arc<any>()
                .innerRadius(0)
                .outerRadius(rScale(d.value))
                .startAngle(i * angleStep)
                .endAngle((i + 1) * angleStep)
                .cornerRadius(2)
                .padAngle(0.04);

            wedges.append('path')
                .attr('d', arc(null)!)
                .attr('fill', color)
                .attr('stroke', color)
                .attr('stroke-width', 1)
                .attr('stroke-opacity', 0.2)
                .style('cursor', 'pointer')
                .on('mouseover', (event) => {
                    d3.select(event.currentTarget)
                        .transition()
                        .duration(200)
                        .attr('stroke-opacity', 1)
                        .attr('stroke-width', 2);

                    const tooltip = d3.select(this.tooltipElement.nativeElement);
                    tooltip.style('display', 'block')
                        .html(`<strong>${d.label}</strong>: ${d.value.toLocaleString()}`);
                })
                .on('mousemove', (event) => {
                    const tooltip = d3.select(this.tooltipElement.nativeElement);
                    tooltip.style('left', (event.offsetX + 10) + 'px')
                        .style('top', (event.offsetY - 30) + 'px');
                })
                .on('mouseout', (event) => {
                    d3.select(event.currentTarget)
                        .transition()
                        .duration(200)
                        .attr('stroke-opacity', 0.4)
                        .attr('stroke-width', 1.5);

                    d3.select(this.tooltipElement.nativeElement).style('display', 'none');
                });
        });
    }

    private getThemeColors() {
        const isDark = this.currentTheme === 'dark';
        return {
            grid: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
            text: isDark ? '#a1a1aa' : '#64748b',
            tooltipBg: isDark ? '#1e1e21' : '#ffffff',
            tooltipText: isDark ? '#fafafa' : '#1e293b'
        };
    }
}
